import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api/client";
import { LoginResult, User } from "../types";

interface AuthContextType {
  user: User | null;
  /** Step 1 of login. Returns ``{requires2fa: false}`` and finalizes
   * the session on success, or ``{requires2fa: true, partialToken}``
   * if the user has TOTP enabled — caller then prompts for a code and
   * calls ``verifyTwoFactor``.
   */
  login: (u: string, p: string) => Promise<LoginResult>;
  /** Step 2 of login (only used when the first step returned requires2fa).
   * Accepts a 6-digit TOTP code or a single-use backup code. */
  verifyTwoFactor: (partialToken: string, code: string) => Promise<void>;
  /** Drop an externally-issued JWT into the session (used by the SSO
   * return page after a successful OIDC round-trip). Validates the
   * token by fetching /auth/me; rejects if that fails. */
  acceptSsoToken: (token: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>(null!);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      api
        .get("/api/v1/auth/me")
        .then((r) => setUser(r.data))
        .catch(() => localStorage.removeItem("token"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Single-session heartbeat. Enforcement is server-side (a newer login
  // rotates the session and every earlier token 401s with
  // "session_superseded"), but without this the superseded tab only finds out
  // when it happens to make a request — an idle dashboard sits there looking
  // signed in, so two devices appear to hold a session at once. Poll a cheap
  // endpoint, and check immediately whenever the tab regains focus, which is
  // exactly when someone comes back to the machine they were evicted from. The
  // 401 interceptor in api/client does the sign-out + redirect.
  useEffect(() => {
    if (!user) return;
    const check = () => {
      if (!localStorage.getItem("token")) return;
      api.get("/api/v1/auth/session").catch(() => {
        /* 401 → interceptor clears the token and bounces to /login */
      });
    };
    const timer = window.setInterval(check, 45_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    // Also check on the first real interaction after a quiet spell. The
    // interval alone is not enough: browsers throttle timers in a background
    // tab and suspend them outright in a frozen one, so an admin who force-signs
    // someone out can watch that person's screen sit there apparently still
    // logged in for minutes. Their session is already dead server-side — every
    // request 401s — but nothing had asked. Touching the keyboard or mouse now
    // asks immediately, so the sign-out becomes visible the moment they return
    // to the machine. Rate-limited so a burst of typing is one request.
    let lastPoke = 0;
    const onInteract = () => {
      const now = Date.now();
      if (now - lastPoke < 10_000) return;
      lastPoke = now;
      check();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    window.addEventListener("pointerdown", onInteract, { capture: true, passive: true });
    window.addEventListener("keydown", onInteract, { capture: true, passive: true });
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", check);
      window.removeEventListener("pointerdown", onInteract, { capture: true });
      window.removeEventListener("keydown", onInteract, { capture: true });
    };
  }, [user]);

  const login = async (username: string, password: string): Promise<LoginResult> => {
    const form = new FormData();
    form.append("username", username);
    form.append("password", password);
    const { data } = await api.post("/api/v1/auth/login", form);
    // 2FA-challenge response. Don't store anything yet — caller will
    // hand the partial_token to verifyTwoFactor.
    if (data?.requires_2fa) {
      return {
        requires2fa: true,
        partialToken: data.partial_token,
        availableFactors: data.available_factors || [],
      };
    }
    localStorage.setItem("token", data.access_token);
    setUser(data.user);
    return { requires2fa: false };
  };

  const verifyTwoFactor = async (partialToken: string, code: string) => {
    const { data } = await api.post("/api/v1/auth/login/verify-2fa", {
      partial_token: partialToken,
      code,
    });
    localStorage.setItem("token", data.access_token);
    setUser(data.user);
  };

  const acceptSsoToken = async (token: string) => {
    localStorage.setItem("token", token);
    try {
      const r = await api.get("/api/v1/auth/me");
      setUser(r.data);
    } catch (e) {
      localStorage.removeItem("token");
      throw e;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, login, verifyTwoFactor, acceptSsoToken, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
